import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { SunHorizon, SignOut, Bell } from '@phosphor-icons/react';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = () => {
      api.get('/notifications/unread-count')
        .then((r) => { if (!cancelled) setUnread(r.data?.count || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000); // poll every 30s — cheap
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.id]);

  const notifPath = user?.role === 'candidate' ? '/candidate/notifications' : null;

  const links = user?.role === 'employer'
    ? [
        { to: '/employer', label: 'Dashboard' },
        { to: '/employer/jobs', label: 'Jobs' },
        { to: '/employer/inbox', label: 'Inbox' },
        { to: '/employer/billing', label: 'Billing' },
      ]
    : user?.role === 'admin'
    ? [
        { to: '/admin', label: 'Admin' },
        { to: '/admin/discover', label: 'Discover' },
        { to: '/admin/fairness', label: 'Fairness' },
      ]
    : user?.role === 'candidate'
    ? [
        { to: '/candidate', label: 'Dashboard' },
        { to: '/candidate/profile', label: 'Profile' },
        { to: '/candidate/jobs', label: 'Jobs' },
        { to: '/candidate/applications', label: 'Applications' },
        { to: '/candidate/assessments', label: 'Assessments' },
      ]
    : [
        { to: '/engine', label: 'See the engine' },
        { to: '/pricing', label: 'Pricing' },
      ];

  return (
    <header
      className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-border"
      data-testid="main-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <SunHorizon size={26} weight="duotone" className="text-primary" />
          <span className="font-display font-black text-xl tracking-tight">HIKARI</span>
          <span className="label-eyebrow ml-2 text-muted-foreground hidden sm:inline">
            Hiring Engine
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                loc.pathname.startsWith(l.to)
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              {notifPath && (
                <Link to={notifPath} className="relative" data-testid="nav-notifications">
                  <Button variant="ghost" size="sm" className="rounded-none px-2">
                    <Bell size={18} weight={unread > 0 ? 'fill' : 'regular'} className={unread > 0 ? 'text-primary' : ''} />
                  </Button>
                  {unread > 0 && (
                    <span
                      className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center"
                      data-testid="nav-notifications-badge"
                    >
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </Link>
              )}
              <span className="hidden sm:flex items-center gap-2 text-sm">
                <span className="label-eyebrow text-muted-foreground">{user.role}</span>
                <span className="font-medium">{user.name}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { logout(); nav('/'); }}
                className="rounded-none"
                data-testid="logout-button"
              >
                <SignOut size={16} className="mr-1" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav('/login')} className="rounded-none" data-testid="nav-login">
                Sign in
              </Button>
              <Button size="sm" onClick={() => nav('/register')} className="rounded-none" data-testid="nav-register">
                Join Hikari
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const Shell = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1">{children}</main>
    <footer className="border-t border-border mt-16">
      <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
        <div>
          <span className="font-display font-black text-foreground">HIKARI</span> · Hire with confidence. Worldwide.
        </div>
        <nav className="flex items-center gap-4 label-eyebrow" data-testid="footer-legal">
          <Link to="/methodology" className="hover:text-foreground" data-testid="footer-methodology">Methodology</Link>
          <Link to="/security" className="hover:text-foreground" data-testid="footer-security">Security</Link>
          <Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy</Link>
        </nav>
        <div className="label-eyebrow">© 2026 · Shining light on capability</div>
      </div>
    </footer>
  </div>
);
